import { Component, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IconType, IoniconsOutline, NotificationType, UiNotification } from '@pt/pt-models';
import { ImportDataService } from '@pt/api-client';
import { Store } from '@ngrx/store';
import { PtCoreActions } from '@pt/pt-core';
import { TranslateService } from '@ngx-translate/core';
import { HttpUtils } from '../utils';

@Component({
  selector: 'pt-import-file-with-check',
  template: `
    <div class='modal-header d-flex flex-column align-items-start'>
      <h4 class='modal-title' [innerHTML]='title | async'></h4>
      <p class='m-0 font-12' [innerHTML]='description | async'></p>
    </div>

    <div class='modal-body'>

      <div class='import-file-body'>
        <div class='icon'>
          <pt-icon [key]="'xls.svg'" [iconType]='SVG' [size]='60'></pt-icon>
          <pt-icon [key]='IconArrowRight' [size]='27'></pt-icon>
          <pt-icon [key]="'store.svg'" [iconType]='SVG' [size]='60'></pt-icon>
        </div>
      </div>

      <p class='message'>
        {{ 'MESSAGE.DOWNLOAD_TEMPLATE' | translate }}
      </p>
      <button class='btn btn-outline-secondary btn-sm'
              (click)='downloadTemplate()'>
        {{ 'LABELS.DOWNLOAD_TEMPLATE' | translate }}
      </button>

      <form class="choose-file">
        <div class="input-group cursor-pointer" (drop)="inputFile.value = ''" (click)=" inputFile.value = ''">
          <span class="input-group-text" style="border-radius: 0">{{ 'MESSAGE.SELECT_FILE' | translate }}</span>
          <input type="text" class="form-control" disabled
                 style="background-color: white; border-radius: 0; margin-left: -2px"
                 [placeholder]="'LABELS.NO_FILE_SELECTED' | translate"
                 [value]="inputFile.files?.item(0)?.name ? inputFile.files?.item(0)?.name : ''">
          <input type="file" class="file-input" style="z-index: 9" accept='.xlsx,.xlsm,.xls'
                 #inputFile (change)="changeListener($event)" (click)="resetImport()">
        </div>
      </form>

      <div *ngIf='errorMessage'>
        <p class='upload-fail-message'>
          {{ 'MESSAGE.UPLOAD_FAIL' | translate }}
        </p>

        <span type='button' class='btn-link' (click)='collapse.toggle()'
              [attr.aria-expanded]='isCollapsedError'
              aria-controls='collapseExample'>
          {{ 'BUTTON.ERROR_DETAIL' | translate }}
        </span>

        <div #collapse='ngbCollapse' [(ngbCollapse)]='isCollapsedError'>
          <div class='card card-error'>
            <div class='card-body'>
              <p class='message text-danger'>
                {{ errorMessage }}
              </p>
            </div>
          </div>
        </div>
      </div>


    </div>

    <div class='modal-footer'>
      <div class='spinner-border spinner-border-sm' role='status' *ngIf='loading'></div>
      <button type='button' class='btn btn-primary btn-sm'
              [disabled]='!enabledImport || loading'
              (click)='saveFileXlsx()'>
        {{ 'BUTTON.LOAD' | translate }}
      </button>
      <button ngbAutofocus type='button' class='btn btn-outline-secondary btn-sm'
              [disabled]='loading'
              (click)='onCancel()'>
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  styles: [`
    .card-error {
      border: 0;
    }

    .card-error .card-body {
      padding: 0;
    }

    .upload-fail-message {
      color: var(--bs-danger);
      margin: 0;
    }

    .icon {
      text-align: center;
      display: flex;
      justify-content: space-around;
      align-items: center;
      width: 160px;
      margin: 25px auto;
    }

    .message {
      font-size: 14px;
      padding: 4px 8px 4px 0;
      margin-bottom: 0;
    }

    .choose-file {
      height: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .file-input {
      left: 0;
      top: 0;
      opacity: 0;
      height: 100%;
      width: 100%;
      position: absolute;
      cursor: pointer;
    }

    .file-input:focus {
      outline: none;
    }
  `]
})
export class ImportFileWithCheckComponent {
  @Input() title: Observable<string>;
  @Input() description: Observable<string> = this.translateService.stream('MESSAGE.NOT_SUPPORT_CHANGE')
  @Input() importService: ImportDataService;

  errorMessage = false;
  enabledImport = false;
  currentFile: File;
  loading: boolean;
  public isCollapsedError = true;

  SVG = IconType.SVG;
  IconFile = IoniconsOutline.file;
  IconArrowRight = IoniconsOutline.arrowRight;
  IconDatabase = IoniconsOutline.database;

  completedLabel = "Loading from file completed!";


  constructor(public activeModal: NgbActiveModal, public store: Store, public translateService: TranslateService) {
    this.translateService.stream('LABELS.IMPORT_COMPLETED').subscribe(
      (success) => this.completedLabel = success
    )
  }

  downloadTemplate() {
    this.importService.downloadTemplate().subscribe(
      (data) => {
        HttpUtils.download(data.body, data.headers);
      }
    );
  }

  resetImport() {
    this.errorMessage = false;
    this.currentFile = undefined;
    const inputFileTemplate = document.getElementById('inputFile');
    if (inputFileTemplate && inputFileTemplate['value']) {
      inputFileTemplate['value'] = null;
    }
  }

  changeListener($event): void {
    this.readThis($event.target);
  }

  readThis(inputValue: any): void {
    const file: File = inputValue.files[0];
    this.checkFileValidity(file);
  }

  checkFileValidity(file: File) {
    this.errorMessage = undefined;
    this.enabledImport = false;
    this.importService.checkImportValidity(file).subscribe(() => {
      this.currentFile = file;
      this.enabledImport = true;
    }, (error) => {
      this.errorMessage = error?.error?.reason;
    });
  }

  saveFileXlsx() {
    this.importService.loadFile(this.currentFile).subscribe((response) => {
      switch (response.type) {
        case 0:
          this.loading = true;
          break;
        case 4:
          this.loading = false;
          this.store.dispatch(PtCoreActions.showNotification({
            notification: {
              type: NotificationType.success,
              title: this.completedLabel,
            } as UiNotification
          }));

          this.activeModal.close(true);
          break;
        default :
          break;
      }
    }, (error) => {
      this.errorMessage = error?.error?.reason;
      this.loading = false;
    });
  }

  onCancel() {
    this.activeModal.close(false);
  }
}

